import React from 'react';
import PropTypes from 'prop-types';
import useNeuroState from '../../state';
import { flatProductList } from '../../utils';
import { createStrictContext } from '../../state/_utils/createContext';
import {usePages} from '../../hooks/usePages';
import {useNeuroMessages} from '../../components/MessageContext';

/**
 * @typedef ProductPageContextObject
 * @property {string} componentClass
 * @property {Object} productGroup - recommendation groups of products from server
 * @property {Array} productsList - product recommendations in an array
 * @property {Array} productsInCart - list of products in the cart
 * @property {Array} productsNotInCart - list of products not in the cart
 */

const createdContext = createStrictContext({
  name: 'ProductPageContext',
});
/** @type {ProductPageContextObject} */
const ProductPageContext = createdContext[0];
/** @returns {ProductPageContextObject} */
const useProductPageContext = createdContext[1];

function ProductPageProvider({ Component, isDetails }) {
  const { products = {}, productCart: stateProductCart } = useNeuroState();
  const { defaultPageTitle } = usePages();
  const { getMessage } = useNeuroMessages();
  const productCart = Array.isArray(stateProductCart) ? new Set(stateProductCart) : stateProductCart;
  const componentClass = isDetails ? 'detailsPage' : 'summaryPage';
  const productsRecommendedList = React.useMemo(
    () => flatProductList(products),
    [products]
  );
  const productsInCart = React.useMemo(
    () => productsRecommendedList.filter( p => (productCart || new Set()).has(p.id.toString()) ),
    [products, productCart]
  );
  const productsNotInCart = React.useMemo(
    () => productsRecommendedList.filter( p => !(productCart || new Set()).has(p.id.toString()) ),
    [products, productCart]
  );

  //Update DOM title for Landing page
  const customPageTitleMsg = getMessage(`${componentClass}.meta.title`);
  document.title = customPageTitleMsg || defaultPageTitle;

  /** @type {ProductPageContextObject} */
  const contextProps = {
    componentClass,
    productGroups: products?.groups || [],
    productsList: productsRecommendedList,
    productsInCart,
    productsNotInCart
  };

  return (
    <ProductPageContext.Provider value={contextProps}>
      <Component {...contextProps} />
    </ProductPageContext.Provider>
  );
}

ProductPageProvider.propTypes = {
  Component: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  isDetails: PropTypes.bool
};

export { useProductPageContext };
export default ProductPageProvider;
